import { type CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@shared/lib/cn';
import { usePortalContainer } from '@shared/lib/portal';

import { CHART_SIZE_CLASS } from './chart.constant';
import { ChartLegendDot } from './chart-legend-dot.ui';
import type { ChartTooltipContent } from './model/chart-tooltip.types';

export type ChartTooltipPlacement = 'left' | 'right';

export interface ChartTooltipProps extends ChartTooltipContent {
  /** 줄 영역의 최대 높이(px). 넘치면 안에서 세로 스크롤(스크롤바 숨김). 없으면 100(`size.chart.tooltip-rows`) */
  maxRowsHeight?: number;
  /** portal 대신 제자리에 그린다(fe-rules 3절). 확인 페이지는 inline 으로 늘어놓는다 */
  inline?: boolean;
  /** 화면 좌표(px). 말풍선을 이 점의 왼쪽 또는 오른쪽, 세로 가운데에 놓는다 */
  anchor?: { x: number; y: number };
  /**
   * anchor 의 어느 쪽에 놓을지. 기본 right.
   * 그쪽에 자리가 없으면(화면 끝) 반대쪽으로 놓고, 위아래는 화면 안으로 당긴다
   */
  placement?: ChartTooltipPlacement;
  /** 마우스가 말풍선에 들어오고 나갈 때 — 그래프가 닫기를 미루는 데 쓴다(`useChartTooltip`) */
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  className?: string;
}

/** anchor 와 말풍선 사이 */
const ANCHOR_GAP = 12;
/** 화면 가장자리와 말풍선 사이 */
const VIEWPORT_MARGIN = 8;

interface Position {
  top: number;
  left: number;
  placement: ChartTooltipPlacement;
}

/** 말풍선 크기를 재어 화면 안에 놓는다: 고른 쪽에 자리가 없으면 반대쪽, 위아래는 화면 안으로 */
function place(
  anchor: { x: number; y: number },
  placement: ChartTooltipPlacement,
  width: number,
  height: number,
): Position {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const rightLeft = anchor.x + ANCHOR_GAP;
  const leftLeft = anchor.x - ANCHOR_GAP - width;
  let side = placement;
  if (side === 'right' && rightLeft + width > viewportWidth - VIEWPORT_MARGIN && leftLeft >= 0) {
    side = 'left';
  } else if (side === 'left' && leftLeft < VIEWPORT_MARGIN && rightLeft + width <= viewportWidth) {
    side = 'right';
  }
  const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - height - VIEWPORT_MARGIN);
  const top = Math.min(Math.max(anchor.y - height / 2, VIEWPORT_MARGIN), maxTop);
  return { top, left: side === 'right' ? rightLeft : leftLeft, placement: side };
}

/**
 * 그래프에 올렸을 때 뜨는 말풍선. 모든 그래프가 같은 것을 쓴다.
 * 제목 + (요약 줄) + (소제목) + 줄 여러 개(색 점 · 이름 · 값, 덧붙임). 너비 240 고정, 꼭지 없음.
 * 줄 영역이 maxRowsHeight(100)를 넘으면 안에서 세로 스크롤하고 스크롤바는 숨긴다(키보드로도 넘길 수 있게 포커스를 받는다).
 * 떠 있는 말풍선은 줄 영역이 넘칠 때만 마우스를 받는다(그때만 스크롤할 일이 있다) — 넘치지 않으면 마우스를 그래프로 흘려
 * 말풍선이 그래프의 다음 요소를 가리지 않게 한다.
 */
export function ChartTooltip({
  title,
  summary,
  subtitle,
  rows,
  maxRowsHeight,
  inline = false,
  anchor,
  placement = 'right',
  onPointerEnter,
  onPointerLeave,
  className,
}: ChartTooltipProps) {
  const container = usePortalContainer();
  const bubbleRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<HTMLUListElement>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [scrollable, setScrollable] = useState(false);
  const floating = !inline && anchor !== undefined;
  const anchorX = anchor?.x;
  const anchorY = anchor?.y;

  useLayoutEffect(() => {
    const list = rowsRef.current;
    setScrollable(list !== null && list.scrollHeight > list.clientHeight + 1);
    const bubble = bubbleRef.current;
    if (!floating || !bubble || anchorX === undefined || anchorY === undefined) return;
    const rect = bubble.getBoundingClientRect();
    setPosition(place({ x: anchorX, y: anchorY }, placement, rect.width, rect.height));
  }, [floating, anchorX, anchorY, placement, title, summary, subtitle, rows]);

  const positionStyle: CSSProperties | undefined = floating
    ? {
        position: 'fixed',
        top: position?.top ?? anchorY,
        left: position?.left ?? anchorX,
        visibility: position ? undefined : 'hidden',
      }
    : undefined;

  const bubble = (
    <div
      ref={bubbleRef}
      role="tooltip"
      data-placement={inline ? undefined : (position?.placement ?? placement)}
      data-scrollable={scrollable || undefined}
      style={positionStyle}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      className={cn(
        CHART_SIZE_CLASS.tooltip,
        'flex flex-col gap-1 rounded-sm bg-dataviz-tooltip px-3 py-2 text-dataviz-on-tooltip shadow-popover',
        !inline && 'z-(--z-tooltip)',
        floating && !scrollable && 'pointer-events-none',
        className,
      )}
    >
      <p className="typo-body-sm-strong">{title}</p>
      {summary ? <p className="typo-caption">{summary}</p> : null}
      {subtitle ? <p className="typo-caption-strong">{subtitle}</p> : null}
      {rows.length > 0 ? (
        <ul
          ref={rowsRef}
          tabIndex={0}
          style={maxRowsHeight === undefined ? undefined : { maxHeight: maxRowsHeight }}
          className={cn(
            'scrollbar-none flex flex-col gap-1 overflow-y-auto focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
            maxRowsHeight === undefined && CHART_SIZE_CLASS.tooltipRows,
          )}
        >
          {rows.map((row, index) => (
            <li key={`${row.name}-${index}`} className="flex shrink-0 flex-col gap-1">
              <div className="flex items-center gap-2 typo-caption">
                {row.color ? <ChartLegendDot {...row.color} /> : null}
                <span className="min-w-0 flex-1 truncate">{row.name}</span>
                {row.value ? <span className="shrink-0">{row.value}</span> : null}
              </div>
              {row.note ? <p className="typo-caption-sm">{row.note}</p> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );

  if (inline) return bubble;
  return createPortal(bubble, container);
}
